import { useState, useMemo } from "react";
// @ts-ignore
import CheckmarkBold from "svg/checkmark-bold.svg";
import Button from "components/Button";
import ProductCard from "components/ProductCard";
import { useArrangement } from "./ArrangementContext";
import KeepsakeDetailModal from "./KeepsakeDetailModal";
import { formatVariantLabel } from "./variantLabels";
import type { KeepsakeProduct } from "./types";
import { useExperimentVariant } from "hooks/useExperimentVariant";

const EXPANDED_KEEPSAKES_FLAG = "at-need-expanded-keepsakes";

type KeepsakeSelectionProps = {
  // Override which keepsakes the grid renders. Defaults to excluding
  // fingerprint products, which are surfaced on the Fingerprints sub-view
  // (ENG-892). Pass a different predicate to render a different subset.
  productFilter?: (keepsake: KeepsakeProduct) => boolean;
  // Fires when the user changes the quantity of any keepsake — including
  // adds, updates via modal, and removes. Hook for analytics on specific
  // product categories without forking the component.
  onSelectionChange?: (
    keepsake: KeepsakeProduct,
    quantity: number,
    previousQuantity: number,
  ) => void;
  // Render products visibly but block interaction. Used by the Fingerprints
  // sub-view to keep the jewelry visible while the customer hasn't yet
  // opted into preservation. Distinct from isReadOnly (CS preview).
  disabled?: boolean;
};

const defaultProductFilter = (k: KeepsakeProduct) => !k.fingerprint;

export default function KeepsakeSelection({
  productFilter = defaultProductFilter,
  onSelectionChange,
  disabled = false,
}: KeepsakeSelectionProps = {}) {
  const {
    state,
    setKeepsakeSelection,
    clearKeepsakeSelection,
    config,
    isReadOnly,
  } = useArrangement();
  const [modalKeepsake, setModalKeepsake] = useState<KeepsakeProduct | null>(
    null,
  );

  const variant = useExperimentVariant(EXPANDED_KEEPSAKES_FLAG, isReadOnly);
  const isExpandedTest = !isReadOnly && variant === "test";

  // Keepsakes tagged cohort-test are visible only to the "test" cohort;
  // already-selected ones always stay visible. Delete with the experiment.
  const sortedKeepsakes = useMemo(() => {
    return [...(config?.keepsakes ?? [])]
      .filter(productFilter)
      .filter(
        (k) =>
          isExpandedTest ||
          !k.cohortTest ||
          state.selectedKeepsakes.some((s) => s.productId === k.id),
      )
      .sort((a, b) => {
        if (a.popular && !b.popular) return -1;
        if (!a.popular && b.popular) return 1;
        return 0;
      });
  }, [
    config?.keepsakes,
    productFilter,
    isExpandedTest,
    state.selectedKeepsakes,
  ]);

  const getSelection = (productId: string) => {
    return state.selectedKeepsakes.find((k) => k.productId === productId);
  };

  const handleSelect = (
    productId: string,
    variantId: string,
    quantity: number,
  ) => {
    const prevQuantity = getSelection(productId)?.quantity ?? 0;
    setKeepsakeSelection(productId, variantId, quantity);
    if (onSelectionChange && quantity !== prevQuantity) {
      const product = config?.keepsakes.find((k) => k.id === productId);
      if (product) onSelectionChange(product, quantity, prevQuantity);
    }
  };

  const handleClear = (productId: string) => {
    const prevQuantity = getSelection(productId)?.quantity ?? 0;
    clearKeepsakeSelection(productId);
    if (onSelectionChange && prevQuantity > 0) {
      const product = config?.keepsakes.find((k) => k.id === productId);
      if (product) onSelectionChange(product, 0, prevQuantity);
    }
  };

  // isReadOnly (CS preview) and disabled (gated cross-sell) both hide
  // interactive buttons. Only `disabled` dims the cards — readOnly cards
  // should render at normal intensity since CS is reviewing real state.
  const hideActions = isReadOnly || disabled;

  return (
    <div>
      <div
        className={`grid grid-cols-1 justify-items-center gap-20 sm:grid-cols-2 md:grid-cols-3 ${
          disabled ? "pointer-events-none opacity-50" : ""
        }`}
        aria-disabled={disabled}
      >
        {sortedKeepsakes.map((keepsake) => {
          const sel = getSelection(keepsake.id);
          const qty = sel?.quantity || 0;
          const selectedVariant = sel
            ? keepsake.variants?.find((v) => v.id === sel.variantId)
            : null;
          const selectedVariantLabel = formatVariantLabel(
            selectedVariant?.name,
          );
          return (
            <ProductCard
              key={keepsake.id}
              name={keepsake.name}
              price={keepsake.price}
              color={keepsake.color}
              image={keepsake.image}
              selected={qty >= 1}
            >
              {qty >= 1 ? (
                <div className="space-y-2">
                  <Button
                    onClick={() => setModalKeepsake(keepsake)}
                    variant="text"
                    fullWidth
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <CheckmarkBold className="h-4 w-4" />
                      Selected
                      {selectedVariantLabel ? ` · ${selectedVariantLabel}` : ""}
                      {` · Qty ${qty}`}
                    </span>
                  </Button>
                  {!hideActions && (
                    <Button
                      onClick={() => handleClear(keepsake.id)}
                      variant="outline"
                      size="sm"
                      fullWidth
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ) : hideActions ? null : (
                <Button
                  onClick={() => setModalKeepsake(keepsake)}
                  variant="tertiary"
                  fullWidth
                >
                  View details
                </Button>
              )}
            </ProductCard>
          );
        })}
      </div>

      <KeepsakeDetailModal
        keepsake={modalKeepsake}
        isOpen={!!modalKeepsake}
        onClose={() => setModalKeepsake(null)}
        onSelect={handleSelect}
        readOnly={isReadOnly}
        currentVariantId={
          modalKeepsake
            ? (getSelection(modalKeepsake.id)?.variantId ?? null)
            : null
        }
        currentQuantity={
          modalKeepsake ? getSelection(modalKeepsake.id)?.quantity : undefined
        }
      />
    </div>
  );
}
